<template>
  <div class="min-h-app-screen bg-gray-50 dark:bg-[#0d1117]">

    <FlashNotifications ref="flash" />

    <!-- ══ MOBILE LAYOUT ══ -->
    <div class="sm:hidden">

      <!-- Mobile header -->
      <div class="bg-white border-b border-gray-100">

        <!-- Title row -->
        <div class="flex items-center justify-between px-4 pt-5 pb-3">
          <div class="flex items-center gap-2.5">
            <h1 class="text-[22px] font-bold text-gray-900 leading-none tracking-tight">Events</h1>
            <span v-if="!store.loading"
              class="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-600 tabular-nums">
              {{ store.events.length }}
            </span>
          </div>
          <router-link to="/events/new" data-turbo-prefetch="false"
            class="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-blue-600/30 active:scale-95 transition-transform">
            <svg class="w-[18px] h-[18px] text-white" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
          </router-link>
        </div>

        <!-- Search -->
        <div class="px-4 pb-3">
          <div class="relative">
            <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
            </svg>
            <input v-model="searchQuery" type="search" placeholder="Search events..."
              class="w-full bg-gray-100 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all" />
          </div>
        </div>

        <!-- Filter pills -->
        <div class="flex gap-2 px-4 pb-3.5 overflow-x-auto no-scrollbar">
          <button v-for="tab in statusTabs" :key="tab.value"
            @click="filterStatus = tab.value"
            class="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0"
            :class="filterStatus === tab.value
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-600'">
            {{ tab.label }}
            <span class="tabular-nums font-bold"
              :class="filterStatus === tab.value ? 'text-blue-200' : 'text-gray-400'">
              {{ tab.count }}
            </span>
          </button>
        </div>
      </div>

      <!-- Mobile events list -->
      <div class="px-4 pt-4 pb-28 space-y-3">

        <!-- Loading skeleton -->
        <template v-if="store.loading">
          <div v-for="i in skeletonCount" :key="i"
            class="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
            <div class="flex items-stretch">
              <div class="w-1 bg-gray-200 flex-shrink-0"></div>
              <div class="flex-1 px-4 py-4 space-y-2">
                <div class="h-4 bg-gray-200 rounded w-2/3"></div>
                <div class="h-3 bg-gray-100 rounded w-1/2"></div>
              </div>
              <div class="flex items-center pr-4">
                <div class="w-9 h-5 bg-gray-200 rounded-full"></div>
              </div>
            </div>
          </div>
        </template>

        <!-- Error state -->
        <template v-else-if="store.error">
          <div class="flex flex-col items-center justify-center py-20 text-center">
            <div class="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
              </svg>
            </div>
            <p class="font-semibold text-gray-900 mb-1">Something went wrong</p>
            <p class="text-sm text-gray-500 mb-4">{{ store.error }}</p>
            <button @click="store.fetchEvents()" class="text-blue-600 font-semibold text-sm">Try again</button>
          </div>
        </template>

        <!-- No events yet -->
        <template v-else-if="store.events.length === 0">
          <div class="flex flex-col items-center justify-center py-24 text-center">
            <div class="w-20 h-20 rounded-3xl bg-blue-50 flex items-center justify-center mb-5">
              <svg class="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
              </svg>
            </div>
            <h2 class="text-xl font-bold text-gray-900 mb-2">No events yet</h2>
            <p class="text-sm text-gray-500 max-w-[240px] mb-6">Create your first event to start accepting bookings from clients.</p>
            <router-link to="/events/new" data-turbo-prefetch="false"
              class="px-6 py-3 bg-blue-600 text-white font-semibold rounded-2xl text-sm shadow-md shadow-blue-600/30">
              Create event
            </router-link>
          </div>
        </template>

        <!-- Events exist -->
        <template v-else>

          <!-- No results from search/filter -->
          <div v-if="filteredEvents.length === 0"
            class="flex flex-col items-center justify-center py-20 text-center">
            <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <svg class="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
              </svg>
            </div>
            <p class="font-semibold text-gray-900 mb-1">No events found</p>
            <p class="text-sm text-gray-400 mb-4">Try a different search or filter.</p>
            <button @click="clearFilters" class="text-blue-600 font-semibold text-sm">Clear filters</button>
          </div>

          <!-- Event cards -->
          <div v-for="event in filteredEvents" :key="event._uid"
            class="bg-white rounded-2xl shadow-sm border border-gray-100"
            :class="{ 'opacity-60': !event.active }">
            <div class="flex items-stretch">

              <!-- Color bar -->
              <div class="w-1 flex-shrink-0 rounded-l-2xl"
                :class="event.active ? event.color : 'bg-gray-200'"></div>

              <!-- Info (tappable) -->
              <router-link :to="`/events/${event.id}/bookings`" data-turbo-prefetch="false"
                class="flex-1 px-4 py-4 min-w-0 active:bg-gray-50 transition-colors">
                <p class="font-semibold text-[15px] text-gray-900 leading-snug truncate">{{ event.name }}</p>
                <div class="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span class="inline-flex items-center gap-1 text-xs text-gray-400">
                    <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                    </svg>
                    {{ event.slot_duration }}
                  </span>
                  <template v-if="event.location_name && event.location_name !== 'None'">
                    <span class="text-gray-200 text-xs">·</span>
                    <span class="text-xs text-gray-400 truncate max-w-[90px]">{{ event.location_name }}</span>
                  </template>
                  <span class="text-gray-200 text-xs">·</span>
                  <span class="text-xs font-medium"
                    :class="event.is_public ? 'text-emerald-500' : 'text-gray-400'">
                    {{ event.is_public ? 'Public' : 'Private' }}
                  </span>
                </div>
              </router-link>

              <!-- Right: toggle + menu -->
              <div class="flex items-center gap-2 pr-3 flex-shrink-0">
                <button type="button"
                  @click.stop="toggleActive(event)"
                  :class="event.active ? 'bg-blue-600' : 'bg-gray-200'"
                  class="relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none">
                  <span :class="event.active ? 'translate-x-[18px]' : 'translate-x-0.5'"
                    class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200" />
                </button>
                <div class="relative">
                  <button @click.stop="toggleMenu(event._uid)"
                    class="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 transition-colors"
                    :class="openMenuKey === event._uid ? 'bg-gray-100 text-gray-600' : 'active:bg-gray-100'">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                    </svg>
                  </button>
                  <EventMenu v-if="openMenuKey === event._uid"
                    :event="event"
                    :app-url="constants.APPLICATION_URL"
                    position="right"
                    @edit="goEdit(event)"
                    @view="goView(event)"
                    @copy="copyUrl(event)"
                    @clone="doClone(event)"
                    @privacy="openPrivacy(event)"
                    @delete="openDelete(event)"
                    @close="openMenuKey = null" />
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- ══ DESKTOP LAYOUT ══ -->
    <div class="hidden sm:block">

      <!-- ── Page header ── -->
      <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <!-- Title row -->
          <div class="flex items-center justify-between pt-5 pb-3 gap-4">
            <div class="min-w-0">
              <div class="flex items-center gap-3">
                <h1 class="text-sm font-semibold text-gray-900 dark:text-white">Events</h1>
                <span v-if="!store.loading"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 tabular-nums">
                  {{ store.events.length }}
                </span>
              </div>
              <div v-if="globalStore.user.id" class="flex items-center gap-1.5 mt-0.5">
                <span class="text-[11px] text-gray-400 dark:text-gray-500 leading-none truncate">{{ profileUrl }}</span>
                <button @click="copyProfileUrl"
                  class="p-0.5 rounded text-gray-400 hover:text-blue-500 transition-colors flex-shrink-0"
                  title="Copy profile URL">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2m-6 12h8a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z"/>
                  </svg>
                </button>
                <Transition name="fade">
                  <span v-if="copied" class="text-xs text-green-500 font-medium flex-shrink-0">Copied!</span>
                </Transition>
              </div>
            </div>

            <router-link to="/events/new" data-turbo-prefetch="false"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm flex-shrink-0">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              <span class="hidden sm:inline">New Event</span>
              <span class="sm:hidden">New</span>
            </router-link>
          </div>

          <!-- Toolbar row -->
          <div class="flex flex-wrap items-center gap-2 pb-4">

            <!-- Search input -->
            <div class="relative w-full sm:w-auto sm:flex-1 sm:max-w-xs">
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
              </svg>
              <input v-model="searchQuery"
                type="search"
                placeholder="Search events..."
                class="w-full h-8 pl-9 pr-3 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow">
            </div>

            <!-- Status filter tabs -->
            <div class="flex items-center h-8 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 gap-0.5">
              <button v-for="tab in statusTabs" :key="tab.value"
                @click="filterStatus = tab.value"
                class="flex items-center h-6 gap-1.5 px-3 text-xs font-semibold rounded-md transition-all whitespace-nowrap"
                :class="filterStatus === tab.value
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
                {{ tab.label }}
                <span class="tabular-nums font-medium text-[10px] min-w-[14px] text-center"
                  :class="filterStatus === tab.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'">
                  {{ tab.count }}
                </span>
              </button>
            </div>

            <!-- Spacer -->
            <div class="hidden sm:block flex-1"></div>

            <!-- Sort select -->
            <select v-model="sortBy"
              class="h-8 pl-3 pr-7 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors cursor-pointer">
              <option value="default">Sort: Default</option>
              <option value="name_asc">Name A to Z</option>
              <option value="name_desc">Name Z to A</option>
              <option value="active_first">Active first</option>
            </select>

            <!-- Layout toggle -->
            <div class="flex items-center h-8 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 gap-0.5">
              <button @click="store.setLayout('grid')"
                :class="store.layout === 'grid'
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'"
                class="w-6 h-6 flex items-center justify-center rounded-md transition-all duration-150" title="Grid view">
                <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M4 4h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 10h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4zM4 16h4v4H4zm6 0h4v4h-4zm6 0h4v4h-4z"/>
                </svg>
              </button>
              <button @click="store.setLayout('table')"
                :class="store.layout === 'table'
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'"
                class="w-6 h-6 flex items-center justify-center rounded-md transition-all duration-150" title="List view">
                <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"/>
                </svg>
              </button>
            </div>

            <!-- Export -->
            <div class="relative">
              <button type="button" @click.stop="toggleMenu('export')"
                class="inline-flex items-center h-8 gap-1.5 px-3 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"/>
                </svg>
                Export
              </button>

              <div v-if="openMenuKey === 'export'" @click.stop
                class="absolute right-0 z-30 mt-1 w-36 bg-white dark:bg-[#1C2128] rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 py-1 text-sm divide-y divide-gray-100 dark:divide-gray-700">
                <button type="button" @click="exportEventsCsv"
                  class="flex items-center gap-2 w-full text-left px-3.5 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/>
                  </svg>
                  CSV
                </button>
                <button type="button" @click="exportEventsJson"
                  class="flex items-center gap-2 w-full text-left px-3.5 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5"/>
                  </svg>
                  JSON
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Content area ── -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        <!-- Loading skeleton -->
        <template v-if="store.loading">
          <div v-if="store.layout === 'grid'"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div v-for="i in skeletonCount" :key="i"
              class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-pulse">
              <div class="h-1 bg-gray-200 dark:bg-gray-700 w-full"></div>
              <div class="p-5 space-y-3">
                <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-full"></div>
                <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-5/6"></div>
                <div class="flex gap-2 pt-2">
                  <div class="h-5 bg-gray-100 dark:bg-gray-800 rounded-full w-20"></div>
                  <div class="h-5 bg-gray-100 dark:bg-gray-800 rounded-full w-16"></div>
                </div>
              </div>
              <div class="border-t border-gray-100 dark:border-gray-700 px-5 py-3 flex justify-between">
                <div class="h-5 w-14 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                <div class="h-4 w-20 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
          </div>
          <div v-else class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-pulse">
            <div class="px-6 py-3 border-b border-gray-100 dark:border-gray-700">
              <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-40"></div>
            </div>
            <div v-for="i in skeletonCount" :key="i"
              class="flex items-center gap-4 px-6 py-4 border-b border-gray-50 dark:border-gray-800 last:border-b-0">
              <div class="w-3 h-3 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
              <div class="flex-1 space-y-1.5">
                <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-1/2"></div>
              </div>
              <div class="hidden md:block h-4 bg-gray-100 dark:bg-gray-800 rounded w-20"></div>
              <div class="hidden md:block h-4 bg-gray-100 dark:bg-gray-800 rounded w-20"></div>
              <div class="h-5 w-9 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
              <div class="h-5 w-5 bg-gray-100 dark:bg-gray-800 rounded"></div>
            </div>
          </div>
        </template>

        <!-- Error state -->
        <template v-else-if="store.error">
          <div class="flex flex-col items-center justify-center py-20 text-center">
            <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
              <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
              </svg>
            </div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
            <button @click="store.fetchEvents()"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors text-sm">
              Try again
            </button>
          </div>
        </template>

        <!-- Empty state: no events at all -->
        <template v-else-if="store.events.length === 0">
          <div class="flex flex-col items-center justify-center py-24 text-center">
            <div class="w-20 h-20 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-6">
              <svg class="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
              </svg>
            </div>
            <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">No events yet</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 max-w-xs mb-8">
              Create your first event to start accepting bookings from clients.
            </p>
            <router-link to="/events/new" data-turbo-prefetch="false"
              class="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors shadow-sm text-sm">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Create your first event
            </router-link>
          </div>
        </template>

        <!-- Events exist: filtered results -->
        <template v-else>

          <!-- No results from search/filter -->
          <div v-if="filteredEvents.length === 0"
            class="flex flex-col items-center justify-center py-20 text-center">
            <div class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
              </svg>
            </div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">No events found</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Try a different search term or change your filter.
            </p>
            <button @click="clearFilters"
              class="px-4 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
              Clear filters
            </button>
          </div>

          <!-- ── Grid layout ── -->
          <Transition name="fade-up" mode="out-in">
            <div v-if="filteredEvents.length > 0 && store.layout === 'grid'"
              class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

              <div v-for="event in filteredEvents" :key="event._uid"
                class="group bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-md dark:hover:shadow-gray-900/40 transition-all duration-200 flex flex-col"
                :class="{ 'opacity-55': !event.active }">

                <!-- Color stripe -->
                <div class="h-1 w-full flex-shrink-0 rounded-t-xl"
                  :class="event.active ? event.color : 'bg-gray-300 dark:bg-gray-600'"></div>

                <div class="p-5 flex-1 flex flex-col">
                  <!-- Name + action menu -->
                  <div class="flex items-start justify-between gap-2 mb-3">
                    <router-link :to="`/events/${event.id}/bookings`" data-turbo-prefetch="false"
                      class="font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors line-clamp-2 leading-snug text-[15px]">
                      {{ event.name }}
                    </router-link>
                    <div class="relative flex-shrink-0">
                      <button @click.stop="toggleMenu(event._uid)"
                        class="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        :class="{ 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300': openMenuKey === event._uid }">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                        </svg>
                      </button>
                      <EventMenu v-if="openMenuKey === event._uid"
                        :event="event"
                        :app-url="constants.APPLICATION_URL"
                        @edit="goEdit(event)"
                        @view="goView(event)"
                        @copy="copyUrl(event)"
                        @clone="doClone(event)"
                        @privacy="openPrivacy(event)"
                        @delete="openDelete(event)"
                        @close="openMenuKey = null" />
                    </div>
                  </div>

                  <!-- Description -->
                  <p class="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed flex-1 mb-3"
                    :class="{ 'italic text-gray-300 dark:text-gray-600': !stripHtml(event.description) }">
                    {{ stripHtml(event.description) || 'No description' }}
                  </p>

                  <!-- Metadata badges -->
                  <div class="flex flex-wrap gap-1.5">
                    <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                      </svg>
                      {{ event.slot_duration }}
                    </span>
                    <span v-if="event.location_name && event.location_name !== 'None'"
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"/>
                      </svg>
                      {{ event.location_name }}
                    </span>
                    <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                      :class="event.is_public
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-500'">
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path v-if="event.is_public" fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm-1.5-9.5a1.5 1.5 0 1 1 3 0v.5h.5a1 1 0 1 1 0 2H7a1 1 0 1 1 0-2h.5v-.5Z" clip-rule="evenodd"/>
                        <path v-else fill-rule="evenodd" d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z" clip-rule="evenodd"/>
                      </svg>
                      {{ event.is_public ? 'Public' : 'Private' }}
                    </span>
                  </div>
                </div>

                <!-- Card footer: toggle + bookings link -->
                <div class="border-t border-gray-100 dark:border-gray-700/70 px-5 py-3 flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <button type="button"
                      @click="toggleActive(event)"
                      :class="event.active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'"
                      class="relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none">
                      <span :class="event.active ? 'translate-x-[18px]' : 'translate-x-0.5'"
                        class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200" />
                    </button>
                    <span class="text-xs font-medium"
                      :class="event.active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'">
                      {{ event.active ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                  <router-link :to="`/events/${event.id}/bookings`" data-turbo-prefetch="false"
                    class="text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors inline-flex items-center gap-1">
                    Bookings
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                    </svg>
                  </router-link>
                </div>
              </div>
            </div>
          </Transition>

          <!-- ── Table layout ── -->
          <Transition name="fade-up" mode="out-in">
            <div v-if="filteredEvents.length > 0 && store.layout === 'table'"
              class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">

              <!-- Table header -->
              <div class="grid grid-cols-[auto_1fr_auto_auto] lg:grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 items-center px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <span class="w-3"></span>
                <span>Event</span>
                <span class="hidden lg:block w-24 text-center">Duration</span>
                <span class="hidden lg:block w-28 text-center">Location</span>
                <span class="w-16 text-center">Status</span>
                <span class="w-20 text-right">Actions</span>
              </div>

              <!-- Event rows -->
              <div v-for="event in filteredEvents" :key="event._uid"
                class="group grid grid-cols-[auto_1fr_auto_auto] lg:grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 items-center px-6 py-3.5 border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50/70 dark:hover:bg-gray-800/50 transition-colors last:border-b-0"
                :class="{ 'opacity-55': !event.active }">

                <!-- Color dot -->
                <div class="w-3 h-3 rounded-full flex-shrink-0"
                  :class="event.active ? event.color : 'bg-gray-300 dark:bg-gray-600'"></div>

                <!-- Name + description + visibility badge -->
                <div class="min-w-0">
                  <router-link :to="`/events/${event.id}/bookings`" data-turbo-prefetch="false"
                    class="font-semibold text-sm text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors line-clamp-1 block">
                    {{ event.name }}
                  </router-link>
                  <div class="flex items-center gap-2 mt-0.5">
                    <p class="text-xs text-gray-400 dark:text-gray-500 line-clamp-1 flex-1 min-w-0">
                      {{ stripHtml(event.description) || 'No description' }}
                    </p>
                    <span class="lg:hidden inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0"
                      :class="event.is_public
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-500'">
                      {{ event.is_public ? 'Public' : 'Private' }}
                    </span>
                  </div>
                </div>

                <!-- Duration -->
                <span class="hidden lg:flex items-center justify-center gap-1 w-24 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full px-2 py-0.5 tabular-nums">
                  <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                  </svg>
                  {{ event.slot_duration }}
                </span>

                <!-- Location -->
                <div class="hidden lg:flex w-28 justify-center">
                  <span v-if="event.location_name && event.location_name !== 'None'"
                    class="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 max-w-full">
                    <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"/>
                    </svg>
                    <span class="truncate">{{ event.location_name }}</span>
                  </span>
                  <span v-else class="text-gray-300 dark:text-gray-600 text-sm">—</span>
                </div>

                <!-- Active toggle -->
                <div class="w-16 flex justify-center">
                  <button type="button"
                    @click="toggleActive(event)"
                    :class="event.active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'"
                    class="relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none">
                    <span :class="event.active ? 'translate-x-[18px]' : 'translate-x-0.5'"
                      class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200" />
                  </button>
                </div>

                <!-- Action buttons -->
                <div class="w-20 flex items-center justify-end gap-1">
                  <a :href="event.absolute_url" target="_blank"
                    class="p-1.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="View public page">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
                    </svg>
                  </a>
                  <div class="relative">
                    <button @click.stop="toggleMenu(event._uid)"
                      class="p-1.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      :class="{ 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300': openMenuKey === event._uid }"
                      title="More actions">
                      <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                      </svg>
                    </button>
                    <EventMenu v-if="openMenuKey === event._uid"
                      :event="event"
                      :app-url="constants.APPLICATION_URL"
                      position="right"
                      @edit="goEdit(event)"
                      @view="goView(event)"
                      @copy="copyUrl(event)"
                      @clone="doClone(event)"
                      @privacy="openPrivacy(event)"
                      @delete="openDelete(event)"
                      @close="openMenuKey = null" />
                  </div>
                </div>
              </div>
            </div>
          </Transition>

        </template>
      </div>

    </div>
    <!-- end desktop layout -->

    <!-- ── Delete confirmation modal ── -->
    <Transition name="modal">
      <div v-if="deleteModal.open"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="deleteModal.open = false">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
        <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-sm w-full p-6 animate-in">
          <div class="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
            </svg>
          </div>
          <h3 class="text-lg font-bold text-gray-900 dark:text-white text-center mb-2">Delete event?</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
            "<strong class="text-gray-700 dark:text-gray-300">{{ deleteModal.event?.name }}</strong>" and all its bookings will be permanently removed.
          </p>
          <div class="flex gap-3">
            <button @click="deleteModal.open = false"
              class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
              Cancel
            </button>
            <button @click="doDelete"
              :disabled="deleting"
              class="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 rounded-lg transition-colors flex items-center justify-center gap-2">
              <svg v-if="deleting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              {{ deleting ? 'Deleting...' : 'Yes, delete' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ── Privacy modal ── -->
    <EventPrivacyModal
      :open="privacyModal.open"
      :is-public="!!privacyModal.event?.is_public"
      :saving="privacySaving"
      @close="privacyModal.open = false"
      @save="savePrivacy" />

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useEventsStore } from 'stores/events.js'
import { useGlobalStore } from 'stores/global.js'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'
import { getCachedEventsCount } from 'composables/useEventsCountCache.js'
import { useFileExport } from 'composables/useFileExport.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import EventMenu from 'controllers/vue_components/events/EventMenu'
import EventPrivacyModal from 'controllers/vue_components/events/EventPrivacyModal'

const router = useRouter()
const store = useEventsStore()
const globalStore = useGlobalStore()
const constants = useConstants()
const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const { downloadCsv, downloadJson } = useFileExport()

// Real count from the last successful load, read synchronously so the very
// first paint already shows the right number of skeleton rows/cards. See
// docs/skeleton_count_caching.md.
const skeletonCount = Math.min(getCachedEventsCount(), 12)

// ── State ──
const openMenuKey = ref(null)
const copied = ref(false)
const deleting = ref(false)
const searchQuery = ref('')
const filterStatus = ref('all')
const sortBy = ref('default')

const deleteModal = ref({ open: false, event: null })
const privacyModal = ref({ open: false, event: null, tempPublic: true })

// ── Computed ──
const profileUrl = computed(() =>
  `${constants.APPLICATION_DOMAIN}/${globalStore.user?.url || ''}`
)

const statusTabs = computed(() => [
  { value: 'all',      label: 'All',      count: store.events.length },
  { value: 'active',   label: 'Active',   count: store.events.filter(e => e.active).length },
  { value: 'inactive', label: 'Inactive', count: store.events.filter(e => !e.active).length },
])

const filteredEvents = computed(() => {
  let events = store.events

  if (filterStatus.value === 'active') {
    events = events.filter(e => e.active)
  } else if (filterStatus.value === 'inactive') {
    events = events.filter(e => !e.active)
  }

  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase()
    events = events.filter(e => e.name.toLowerCase().includes(q))
  }

  if (sortBy.value === 'name_asc') {
    events = [...events].sort((a, b) => a.name.localeCompare(b.name))
  } else if (sortBy.value === 'name_desc') {
    events = [...events].sort((a, b) => b.name.localeCompare(a.name))
  } else if (sortBy.value === 'active_first') {
    events = [...events].sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0))
  }

  return events
})

// ── Lifecycle ──
onMounted(() => {
  store.fetchIfStale()
  document.addEventListener('click', closeMenu)
})

onUnmounted(() => {
  document.removeEventListener('click', closeMenu)
})

// ── Helpers ──
function stripHtml(html) {
  if (!html) return ''
  return html.replace(/<[^>]*>/g, '').trim()
}

function clearFilters() {
  searchQuery.value = ''
  filterStatus.value = 'all'
}

function closeMenu() {
  if (openMenuKey.value !== null) openMenuKey.value = null
}

function toggleMenu(key) {
  openMenuKey.value = openMenuKey.value === key ? null : key
}

async function copyProfileUrl() {
  await navigator.clipboard.writeText(`${constants.APPLICATION_URL}/${globalStore.user?.url || ''}`)
  copied.value = true
  setTimeout(() => { copied.value = false }, 2000)
}

function copyUrl(event) {
  openMenuKey.value = null
  navigator.clipboard.writeText(constants.APPLICATION_URL + event.absolute_url)
  flash.value?.show('Link copied to clipboard', 'notice')
}

function goEdit(event) { router.push(`/events/${event.id}/edit`) }
function goView(event) { window.open(event.absolute_url, '_blank') }

function exportableEventRows() {
  return filteredEvents.value.map(event => ({
    active: event.active,
    name: event.name,
    description: event.description ? event.description.replace(/<br\s*\/?>/gi, '\n') : '',
    public_url: constants.APPLICATION_URL + event.absolute_url,
    slot_duration: event.slot_duration,
    padding_duration: event.padding_duration,
    location: event.location_name,
    add_to_calendar: event.connected_calendar_email || '',
    id: event.id,
    created_at: event.created_at,
    color: event.color,
  }))
}

function exportEventsCsv() {
  const headers = ['Active', 'Name', 'Description', 'Public URL', 'Slot Duration', 'Padding Duration',
    'Location', 'Add to calendar', 'ID', 'Created At', 'Color']
  const rows = exportableEventRows().map(row => Object.values(row))

  downloadCsv(headers, rows, `events-${Date.now()}.csv`)
  openMenuKey.value = null
}

function exportEventsJson() {
  downloadJson(exportableEventRows(), `events-${Date.now()}.json`)
  openMenuKey.value = null
}

async function toggleActive(event) {
  const newActive = !event.active
  store.updateEvent(event.id, { active: newActive })
  const res = await axiosRequest('PUT', `/api/v1/events/${event.id}`, { event: { active: newActive } }, false)
  if (!res.js_error && res.response.success) {
    const msg = newActive
      ? constants.I18N?.notifications?.events?.event_activated || 'Event activated'
      : constants.I18N?.notifications?.events?.event_deactivated || 'Event deactivated'
    flash.value?.show(msg, 'notice')
  } else {
    store.updateEvent(event.id, { active: !newActive })
    flash.value?.show('Failed to update event', 'alert')
  }
}

async function doClone(event) {
  openMenuKey.value = null
  const res = await axiosRequest('POST', `/api/v1/events/${event.id}/clone`, {}, true)
  if (!res.js_error && res.response.success) {
    store.addEvent(res.response.data)
    store.invalidate()
  }
}

function openDelete(event) {
  deleteModal.value = { open: true, event }
  openMenuKey.value = null
}

async function doDelete() {
  if (!deleteModal.value.event) return
  deleting.value = true
  const key = deleteModal.value.event.id
  const res = await axiosRequest('DELETE', `/api/v1/events/${key}`, {}, false)
  deleting.value = false
  if (!res.js_error && res.response.success) {
    store.removeEvent(key)
    deleteModal.value.open = false
    flash.value?.show('Event deleted', 'notice')
  } else {
    flash.value?.show('Failed to delete event', 'alert')
  }
}

function openPrivacy(event) {
  privacyModal.value = { open: true, event, tempPublic: event.is_public }
  openMenuKey.value = null
}

const privacySaving = ref(false)

async function savePrivacy(tempPublic) {
  const { event } = privacyModal.value
  privacySaving.value = true
  const res = await axiosRequest('PUT', `/api/v1/events/${event.id}`, { event: { is_public: tempPublic } }, false)
  privacySaving.value = false
  if (!res.js_error && res.response.success) {
    store.updateEvent(event.id, { is_public: tempPublic, tempPublic })
    privacyModal.value.open = false
    flash.value?.show(tempPublic ? 'Event set to public' : 'Event set to private', 'notice')
  } else {
    flash.value?.show('Failed to update privacy', 'alert')
  }
}
</script>

<style scoped>
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
.no-scrollbar::-webkit-scrollbar { display: none; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.fade-up-enter-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.fade-up-enter-from { opacity: 0; transform: translateY(8px); }

.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .animate-in { transition: transform 0.2s ease; }
.modal-enter-from .animate-in { transform: scale(0.96) translateY(8px); }
</style>
